'use client';

import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '@/utils/analytics';
import { cn } from '@/utils/utils';
import type { NavDropdownProps } from './types';

export function NavDropdown({
  items,
  title,
  allItemsHref,
  allItemsLabel = `All ${title}`,
  trackingCategory,
}: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleDropdownItemClick = (itemTitle: string) => {
    trackEvent({
      action: 'dropdown_item_click',
      category: trackingCategory || title.toLowerCase(),
      label: itemTitle,
      value: 1,
    });
    setIsOpen(false);
  };

  const handleAllItemsClick = () => {
    trackEvent({
      action: 'all_items_click',
      category: trackingCategory || title.toLowerCase(),
      label: allItemsLabel,
      value: 1,
    });
    setIsOpen(false);
  };

  const handleTriggerClick = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none rounded-md transition-colors"
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {title}
        <ChevronDown
          className={cn(
            'w-4 h-4 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[200px] md:min-w-[500px] lg:min-w-[600px] animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <div className="flex flex-row gap-2 px-4 pt-4">
            <a
              href={allItemsHref}
              className="text-sm font-medium text-black hover:text-blue-800 transition-colors"
              onClick={handleAllItemsClick}
            >
              {allItemsLabel}
            </a>
          </div>

          <div className="grid gap-3 p-4 md:grid-cols-1 lg:grid-cols-2">
            {items.map((item) => (
              <a
                key={item.title}
                className={cn(
                  'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-neutral-100 focus:bg-neutral-100',
                )}
                href={item.href}
                onClick={() => handleDropdownItemClick(item.title)}
              >
                <div className="flex flex-row gap-2">
                  <Image
                    src={item.image}
                    alt={item.title}
                    width={100}
                    height={100}
                    className="w-16 h-16 object-cover rounded-sm flex-shrink-0"
                  />
                  <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium leading-none">
                      {item.title}
                    </div>
                    <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
